import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Skeleton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ArrowDownwardRounded,
	ArrowUpwardRounded,
	AssignmentOutlined,
	AssignmentTurnedInOutlined,
	CircleOutlined,
	ErrorOutlineRounded,
	EventRepeatOutlined,
	HistoryRounded,
	HourglassBottomOutlined,
	InfoOutlined,
	NotificationsActiveOutlined,
	ReportOutlined,
	SupportAgentOutlined,
	TimerOutlined,
	VisibilityOutlined,
	WarningAmberRounded,
} from "@mui/icons-material";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip as TooltipRecharts,
	XAxis,
	YAxis,
} from "recharts";
import dayjs from "dayjs";
import DatagridComponent from "../../datagridComponent";
import IconeTipoAlerta from "../iconeTipoAlerta";
import { buscarIncidentesEquipamento } from "../context/apiIncidentes";
import { agruparAlertasPorDia } from "../../../utils/centralDeAlertas/correlacao";
import {
	SEVERIDADES,
	LABEL_SEVERIDADE,
	LABEL_TIPO_EVENTO,
	formatarDuracaoMinutos,
} from "../../../utils/centralDeAlertas/labels";
import {
	corSeveridade,
	corStatusIncidente,
	corTipoEvento,
	corCategoriaInteligente,
	chipSemantico,
} from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Modal "Analisar padrão" (ação da detecção de falha recorrente nos Alertas
// inteligentes): análise dos alertas de UM equipamento nos últimos 30 dias,
// independente do período filtrado na tela — padrão se enxerga com
// histórico, não com a janela curta do dashboard. Os dados vêm da mesma rota
// real /find/centralAlertas/incidentes (ver buscarIncidentesEquipamento).

const JANELA_ANALISE_DIAS = 30;
// Skeletons espelham o layout real: 5 cards de indicador, título + gráfico,
// título + cabeçalho e linhas da tabela.
const INDICADORES_SKELETON = 5;
const LINHAS_SKELETON = 6;

// Mesmos ícones de severidade dos chips da TabelaIncidentes.
const ICONE_SEVERIDADE = {
	critico: WarningAmberRounded,
	alto: ErrorOutlineRounded,
	medio: ReportOutlined,
	baixo: InfoOutlined,
	informativo: CircleOutlined,
};

const FAIXAS_HORARIO = [
	{ rotulo: "madrugada (0h–6h)", inicio: 0, fim: 6 },
	{ rotulo: "manhã (6h–12h)", inicio: 6, fim: 12 },
	{ rotulo: "tarde (12h–18h)", inicio: 12, fim: 18 },
	{ rotulo: "noite (18h–24h)", inicio: 18, fim: 24 },
];

function media(valores) {
	const validos = (valores || []).filter(
		(valor) => valor !== null && valor !== undefined && !Number.isNaN(valor),
	);
	if (!validos.length) return null;
	return validos.reduce((soma, valor) => soma + valor, 0) / validos.length;
}

export default function AnalisePadrao({ aberto, equipamento, cliente, onFechar }) {
	const theme = useTheme();
	const styles = useStyles(theme);
	const [incidentes, setIncidentes] = useState([]);
	const [carregando, setCarregando] = useState(true);

	const equipamentoId = equipamento?.id || null;

	// Busca o histórico ao abrir (e refaz se abrir para outro equipamento).
	useEffect(() => {
		if (!aberto || !equipamentoId) return undefined;
		const controller = new AbortController();
		setCarregando(true);
		setIncidentes([]);

		buscarIncidentesEquipamento({
			equipamentoId,
			inicio: dayjs().subtract(JANELA_ANALISE_DIAS, "day"),
			fim: dayjs(),
			signal: controller.signal,
		})
			.then((resultado) => {
				if (!controller.signal.aborted) setIncidentes(resultado);
			})
			.catch((error) => {
				if (!controller.signal.aborted) {
					console.error(
						"Erro ao buscar histórico do equipamento para análise de padrão:",
						error,
					);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) setCarregando(false);
			});

		return () => controller.abort();
	}, [aberto, equipamentoId]);

	const analise = useMemo(() => {
		const lista = incidentes || [];
		if (!lista.length) return null;

		// Alertas em ordem cronológica para medir os intervalos entre eles.
		const alertasAsc = lista
			.map((incidente) => dayjs(incidente.dataHoraQueda).valueOf())
			.sort((a, b) => a - b);
		const intervalosMin = alertasAsc
			.slice(1)
			.map((alerta, indice) => (alerta - alertasAsc[indice]) / 60000);

		const mttrs = lista.map((incidente) => incidente.mttr);

		// Faixa de horário com mais alertas.
		const contagemPorFaixa = FAIXAS_HORARIO.map((faixa) => ({
			...faixa,
			alertas: lista.filter((incidente) => {
				const hora = dayjs(incidente.dataHoraQueda).hour();
				return hora >= faixa.inicio && hora < faixa.fim;
			}).length,
		}));
		const faixaMaisComum = contagemPorFaixa.reduce(
			(maior, faixa) => (faixa.alertas > maior.alertas ? faixa : maior),
			contagemPorFaixa[0],
		);

		return {
			totalAlertas: lista.length,
			abertosAgora: lista.filter((incidente) => incidente.aberto).length,
			mttrMedio: media(mttrs),
			maiorDuracao: mttrs.some(
				(valor) => valor !== null && valor !== undefined,
			)
				? Math.max(...mttrs.filter((valor) => valor !== null && valor !== undefined))
				: null,
			intervaloMedio: media(intervalosMin),
			faixaMaisComum: faixaMaisComum.alertas > 0 ? faixaMaisComum : null,
		};
	}, [incidentes]);

	const dadosGrafico = useMemo(
		() =>
			agruparAlertasPorDia(
				incidentes,
				dayjs().subtract(JANELA_ANALISE_DIAS, "day"),
				dayjs(),
			),
		[incidentes],
	);

	// Uma linha por alerta, com os campos já derivados — o DataGrid ordena
	// pelos valores crus (ms/minutos) e formata só na exibição.
	// Ordem padrão: alertas EM ABERTO primeiro (são os acionáveis), depois os
	// demais, ambos do mais recente para o mais antigo. Clicar num cabeçalho
	// reordena normalmente — esta é a ordem inicial, não uma trava.
	const linhasAlertas = useMemo(
		() =>
			(incidentes || [])
				.map((incidente) => ({
					id: incidente.id,
					severidade: incidente.severidade,
					alerta: incidente.alerta?.titulo || null,
					tipoAlertaChave: incidente.alerta?.tipoAlertaChave || null,
					tipoAlertaNome: incidente.alerta?.tipoAlertaNome || null,
					reconhecido: Boolean(incidente.alerta?.reconhecido),
					inicio: dayjs(incidente.dataHoraQueda).valueOf(),
					fim: incidente.recuperacao
						? dayjs(incidente.recuperacao).valueOf()
						: null,
					duracao: incidente.mttr,
					atendimento: Boolean(incidente.atendimento),
					osCriada: Boolean(incidente.os),
					osConcluida: Boolean(incidente.os?.dataHoraConclusao),
					recuperado: Boolean(incidente.recuperacao),
					aberto: Boolean(incidente.aberto),
				}))
				.sort((a, b) => {
					if (a.aberto !== b.aberto) return a.aberto ? -1 : 1;
					return b.inicio - a.inicio;
				}),
		[incidentes],
	);

	const colunas = useMemo(
		() => [
			{
				field: "severidade",
				headerName: "Severidade",
				width: 125,
				// Ordena pela gravidade (ordem de SEVERIDADES), não pelo alfabeto.
				sortComparator: (a, b) =>
					SEVERIDADES.indexOf(a) - SEVERIDADES.indexOf(b),
				renderCell: (celula) => {
					const IconeSeveridade =
						ICONE_SEVERIDADE[celula.value] || CircleOutlined;
					const cor = corSeveridade(theme, celula.value);
					return (
						<Chip
							size="small"
							icon={<IconeSeveridade />}
							label={LABEL_SEVERIDADE[celula.value] || celula.value}
							sx={{ ...chipSemantico(theme, cor), ...styles.chipAlerta }}
						/>
					);
				},
			},
			{
				field: "alerta",
				headerName: "Alerta",
				flex: 1.4,
				minWidth: 220,
				// Ícone do padrão global do catálogo (tipo do alerta no Tooltip,
				// cor da severidade) + nome completo no Tooltip do texto — a
				// célula corta com reticências.
				renderCell: (celula) => (
					<Box sx={styles.linhaTituloAlerta}>
						<IconeTipoAlerta
							chave={celula.row.tipoAlertaChave}
							nome={celula.row.tipoAlertaNome}
							sx={{ color: corSeveridade(theme, celula.row.severidade) }}
						/>
						<Tooltip title={celula.value || ""} arrow>
							<Typography variant="caption" sx={styles.tituloAlertaTabela}>
								{celula.value || "—"}
							</Typography>
						</Tooltip>
					</Box>
				),
			},
			{
				field: "inicio",
				headerName: "Início",
				width: 110,
				// Formato curto (a janela é de 30 dias — ano e segundos não
				// acrescentam); mesmo formato das datas da TabelaIncidentes.
				// x-data-grid v7: valueGetter/valueFormatter é (value, row).
				valueFormatter: (value) => dayjs(value).format("DD/MM HH:mm"),
			},
			{
				field: "fim",
				headerName: "Fim",
				width: 110,
				valueFormatter: (value) =>
					value ? dayjs(value).format("DD/MM HH:mm") : "Em aberto",
			},
			{
				field: "duracao",
				headerName: "Duração",
				width: 120,
				valueFormatter: (value) => formatarDuracaoMinutos(value),
			},
			{
				field: "atendimento",
				headerName: "Linha do tempo",
				width: 170,
				sortable: false,
				// Um ícone por marco, na MESMA ordem e cores da linha do tempo do
				// incidente: colorido quando registrado, esmaecido quando não —
				// o rótulo fica no Tooltip (nunca depender só da cor).
				renderCell: (celula) => {
					const marcos = [
						{
							chave: "queda",
							rotulo: LABEL_TIPO_EVENTO.queda,
							presente: true,
							Icone: ArrowDownwardRounded,
							cor: corTipoEvento(theme, "queda"),
						},
						{
							chave: "reconhecimento",
							rotulo: "Reconhecimento",
							presente: celula.row.reconhecido,
							Icone: VisibilityOutlined,
							cor: corStatusIncidente(theme, "reconhecido"),
						},
						{
							chave: "atendimento_aberto",
							rotulo: LABEL_TIPO_EVENTO.atendimento_aberto,
							presente: celula.row.atendimento,
							Icone: SupportAgentOutlined,
							cor: corTipoEvento(theme, "atendimento_aberto"),
						},
						{
							chave: "os_criada",
							rotulo: LABEL_TIPO_EVENTO.os_criada,
							presente: celula.row.osCriada,
							Icone: AssignmentOutlined,
							cor: corTipoEvento(theme, "os_criada"),
						},
						{
							chave: "os_concluida",
							rotulo: LABEL_TIPO_EVENTO.os_concluida,
							presente: celula.row.osConcluida,
							Icone: AssignmentTurnedInOutlined,
							cor: corTipoEvento(theme, "os_concluida"),
						},
						{
							chave: "recuperacao",
							rotulo: LABEL_TIPO_EVENTO.recuperacao,
							presente: celula.row.recuperado,
							Icone: ArrowUpwardRounded,
							cor: corTipoEvento(theme, "recuperacao"),
						},
					];
					return (
						<Box sx={styles.atendimentoAlerta}>
							{marcos.map(({ chave, rotulo, presente, Icone, cor }) => (
								<Tooltip
									key={chave}
									title={presente ? rotulo : `${rotulo} — não registrado`}
									arrow
								>
									<Icone
										sx={styles.iconeMarcoTabela(presente ? cor : null)}
									/>
								</Tooltip>
							))}
						</Box>
					);
				},
			},
		],
		[theme, styles],
	);

	// Mesmo esqueleto dos cards de KPI da Central (quadrado do ícone + rótulo
	// + valor), com as cores semânticas da feature.
	const indicadores = analise
		? [
			{
				rotulo: "Alertas (30 dias)",
				valor: String(analise.totalAlertas),
				Icone: NotificationsActiveOutlined,
				cor: corTipoEvento(theme, "queda"),
			},
			{
				rotulo: "Abertos agora",
				valor: String(analise.abertosAgora),
				Icone: ErrorOutlineRounded,
				cor: corSeveridade(theme, "critico"),
			},
			{
				rotulo: "MTTR médio",
				valor: formatarDuracaoMinutos(analise.mttrMedio),
				Icone: TimerOutlined,
				cor: corTipoEvento(theme, "recuperacao"),
			},
			{
				rotulo: "Maior duração",
				valor: formatarDuracaoMinutos(analise.maiorDuracao),
				Icone: HourglassBottomOutlined,
				cor: corSeveridade(theme, "alto"),
			},
			{
				rotulo: "Intervalo médio",
				valor: formatarDuracaoMinutos(analise.intervaloMedio),
				Icone: EventRepeatOutlined,
				cor: corCategoriaInteligente(theme, "falha_recorrente"),
			},
		]
		: [];

	const renderConteudo = () => {
		if (carregando) {
			return (
				<Box sx={styles.conteudo}>
					<Box sx={styles.linhaIndicadores}>
						{Array.from({ length: INDICADORES_SKELETON }).map((_, indice) => (
							<Skeleton
								key={`skeleton-indicador-${indice}`}
								variant="rounded"
								sx={styles.skeletonIndicador}
							/>
						))}
					</Box>

					<Box>
						<Skeleton variant="rounded" sx={styles.skeletonTituloSecao} />
						<Skeleton variant="rounded" sx={styles.skeletonGrafico} />
					</Box>

					<Box>
						<Skeleton variant="rounded" sx={styles.skeletonTituloSecao} />
						<Box sx={styles.skeletonTabela}>
							<Skeleton
								variant="rounded"
								sx={styles.skeletonCabecalhoTabela}
							/>
							{Array.from({ length: LINHAS_SKELETON }).map((_, indice) => (
								<Skeleton
									key={`skeleton-linha-${indice}`}
									variant="rounded"
									sx={styles.skeletonLinha}
								/>
							))}
						</Box>
					</Box>
				</Box>
			);
		}

		if (!incidentes.length) {
			return (
				<Box sx={styles.estadoVazio}>
					<Typography color="text.secondary">
						{`Nenhum alerta registrado nos últimos ${JANELA_ANALISE_DIAS} dias.`}
					</Typography>
				</Box>
			);
		}

		return (
			<Box sx={styles.conteudo}>
				<Box sx={styles.linhaIndicadores}>
					{indicadores.map(({ rotulo, valor, Icone, cor }) => (
						<Box key={rotulo} sx={styles.indicador(cor)}>
							<Box sx={styles.quadradoIcone(cor)}>
								<Icone />
							</Box>
							<Box sx={styles.textosIndicador}>
								<Typography sx={styles.valorIndicador}>{valor}</Typography>
								<Typography sx={styles.rotuloIndicador}>{rotulo}</Typography>
							</Box>
						</Box>
					))}
				</Box>

				{analise?.faixaMaisComum && (
					<Box sx={styles.observacao}>
						<HistoryRounded fontSize="small" />
						<Typography variant="caption">
							{`Os alertas se concentram na ${analise.faixaMaisComum.rotulo}: ${analise.faixaMaisComum.alertas} de ${analise.totalAlertas} no período.`}
						</Typography>
					</Box>
				)}

				<Box>
					<Box sx={styles.cabecalhoSecao}>
						<Typography variant="mySubtitle">Alertas por dia</Typography>

					</Box>
					<Box sx={styles.areaGrafico}>
						<ResponsiveContainer width="100%" height={180}>
							<BarChart data={dadosGrafico} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
								<CartesianGrid
									strokeDasharray="3 3"
									stroke={theme.palette.divider}
									vertical={false}
								/>
								<XAxis
									dataKey="dia"
									tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
									minTickGap={16}
									axisLine={false}
									tickLine={false}
								/>
								<YAxis
									allowDecimals={false}
									tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
									axisLine={false}
									tickLine={false}
								/>
								<TooltipRecharts
									formatter={(valor, severidade) => [
										valor,
										LABEL_SEVERIDADE[severidade] || severidade,
									]}
									contentStyle={styles.tooltipGrafico}
									cursor={styles.cursorGrafico}
								/>
								{SEVERIDADES.map((severidade) => (
									<Bar
										key={severidade}
										dataKey={severidade}
										stackId="alertas"
										fill={corSeveridade(theme, severidade)}
									/>
								))}
							</BarChart>
						</ResponsiveContainer>
					</Box>
				</Box>

				<Box>
					<Box sx={styles.cabecalhoSecao}>
						<Typography variant="mySubtitle">
							{incidentes.length === 1
								? "Alertas do período (1)"
								: `Alertas do período (${incidentes.length})`}
						</Typography>
					</Box>
					{/* Todos os alertas dos 30 dias no DataGrid compartilhado
					    (ordenação por coluna + paginação de 25). */}
					<Box sx={styles.areaTabela}>
						<DatagridComponent
							rows={linhasAlertas}
							columns={colunas}
							isPagination
							sxDatagrid={styles.fonteTabela}
						/>
					</Box>
				</Box>
			</Box>
		);
	};

	return (
		<Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="md">
			{/* Cabeçalho no padrão dos cards da feature: título + caption à
			    esquerda, MetricInfo (ⓘ) à direita. */}
			<DialogTitle sx={styles.linhaCabecalho}>
				<Box sx={styles.titulo}>
					<Typography variant="mySubtitle">
						{`Análise de padrão — ${equipamento?.nome || "—"}`}
					</Typography>
					<Typography variant="caption" sx={styles.subtitulo}>
						{`${cliente?.nome || "—"} · Últimos ${JANELA_ANALISE_DIAS} dias, independente do período filtrado na tela`}
					</Typography>
				</Box>
				<MetricInfo theme={theme} guide={GUIDES_CENTRAL.analisePadrao} />
			</DialogTitle>
			<DialogContent>{renderConteudo()}</DialogContent>
			<DialogActions>
				<Button onClick={onFechar}>Fechar</Button>
			</DialogActions>
		</Dialog>
	);
}
